import type { PlayableSchema } from '../../playable-api';

/* How the template settings form reads a schema property (pure, so it is testable without the form). */

/** Whether a string property holds a colour (an explicit format, or a hex pattern). Pure. */
export const isColorSchema = (schema: PlayableSchema): boolean =>
  schema.format === 'color' || (schema.type === 'string' && (schema.pattern ?? '').includes('#'));

/** Whether a property is free text a player reads (a string that is not a choice, a colour or a link), like Bonus
 *  Meter's unlocked label. Pure. */
const isFreeText = (schema: PlayableSchema): boolean =>
  schema.type === 'string' && !schema.enum && !isColorSchema(schema) && schema.format !== 'uri';

/**
 * A mini-game schema without its free-text fields, at any depth (objects and array items): a mini-game's on-screen
 * text is locked (see `mini-game-copy.ts`), so the settings form never offers it. The form keeps every other key of
 * the config as it is, so a text value already stored rides along and the lock overrides it where it is used. Pure.
 */
export const withoutFreeText = (schema: PlayableSchema): PlayableSchema => {
  const next: PlayableSchema = { ...schema };
  if (schema.properties) {
    const kept = Object.entries(schema.properties).filter(([, prop]) => !isFreeText(prop));
    next.properties = Object.fromEntries(kept.map(([key, prop]) => [key, withoutFreeText(prop)]));
    if (schema.required) next.required = schema.required.filter((key) => key in (next.properties ?? {}));
  }
  if (schema.items) next.items = withoutFreeText(schema.items);
  return next;
};
